import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { getRunnerSchedule, type RunnerSchedule } from '@/api/schedules';
import { schedulesIncluded } from '@/lib/schedule';
import { useOrganizationStore } from '@/stores/organization';
import { useSubscriptionStore } from '@/stores/subscription';

export type ScheduleState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; schedule: RunnerSchedule | null };

// The runner list has no schedules, so each runner needs a request; a few at a time keep a large dashboard responsive.
const MAX_CONCURRENT_LOADS = 4;
const CLOCK_INTERVAL_MS = 30_000;

/**
 * Schedules of the dashboard's runners, loaded once per runner while the selected organization's plan includes
 * schedules. Runner polling does not reload them; changes made here are applied with `set`.
 */
export function useRunnerSchedules(runnerIds: () => string[]) {
  const subscription = useSubscriptionStore();
  const organizations = useOrganizationStore();
  const available = computed(
    () =>
      subscription.organizationId !== undefined &&
      subscription.organizationId === organizations.selectedId &&
      schedulesIncluded(subscription.subscription)
  );

  const states = ref<Record<string, ScheduleState>>({});
  const now = ref(new Date());
  let queue: string[] = [];
  let running = 0;
  // Bumped when schedules become unavailable or the view unmounts, so late responses are dropped.
  let generation = 0;
  let clock: ReturnType<typeof setInterval> | undefined;

  function pump() {
    while (running < MAX_CONCURRENT_LOADS && queue.length > 0) {
      void load(queue.shift()!, generation);
    }
  }

  async function load(runnerId: string, context: number) {
    running++;
    try {
      const schedule = await getRunnerSchedule(runnerId);
      if (context === generation && runnerId in states.value) {
        states.value[runnerId] = { status: 'loaded', schedule };
      }
    } catch {
      if (context === generation && runnerId in states.value) {
        states.value[runnerId] = { status: 'error' };
      }
    } finally {
      if (context === generation) {
        running--;
        pump();
      }
    }
  }

  function enqueue(runnerId: string) {
    states.value[runnerId] = { status: 'loading' };
    queue.push(runnerId);
  }

  watch(
    [available, () => runnerIds().join(',')],
    ([isAvailable]) => {
      if (!isAvailable) {
        generation++;
        running = 0;
        queue = [];
        states.value = {};
        clearInterval(clock);
        clock = undefined;
        return;
      }
      if (clock === undefined) {
        now.value = new Date();
        clock = setInterval(() => (now.value = new Date()), CLOCK_INTERVAL_MS);
      }
      const ids = runnerIds();
      const present = new Set(ids);
      for (const id of Object.keys(states.value)) {
        if (!present.has(id)) delete states.value[id];
      }
      queue = queue.filter((id) => present.has(id));
      for (const id of ids) if (!(id in states.value)) enqueue(id);
      pump();
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    generation++;
    clearInterval(clock);
  });

  /** Null when schedules are not available, so nothing about them is shown. */
  function stateFor(runnerId: string): ScheduleState | null {
    if (!available.value) return null;
    return states.value[runnerId] ?? { status: 'loading' };
  }

  function set(runnerId: string, schedule: RunnerSchedule | null) {
    if (runnerId in states.value) states.value[runnerId] = { status: 'loaded', schedule };
  }

  function reload(runnerId: string) {
    if (!available.value || !(runnerId in states.value)) return;
    queue = queue.filter((id) => id !== runnerId);
    enqueue(runnerId);
    pump();
  }

  return { available, now, stateFor, set, reload };
}
